import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, Package } from "lucide-react";
import { api } from "@/lib/api";
import { startPayfastCheckout } from "@/lib/payments";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHeader, Loading, EmptyState, Button, Field } from "@/components/brand";
import { formatR } from "@/lib/format";

const DELIVERY = 60;

export default function Shop() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const [cart, setCart] = useState({}); // productId -> { product, qty }
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("payfast");
  const [addr, setAddr] = useState({ name: "", phone: "", line1: "", city: "", postal_code: "" });

  const items = Object.values(cart);
  const subtotal = items.reduce((a, i) => a + i.product.price * i.qty, 0);
  const count = items.reduce((a, i) => a + i.qty, 0);

  const add = (p) => setCart((c) => ({ ...c, [p.id]: { product: p, qty: Math.min((c[p.id]?.qty || 0) + 1, p.stock) } }));
  const dec = (p) => setCart((c) => { const q = (c[p.id]?.qty || 0) - 1; const n = { ...c }; if (q <= 0) delete n[p.id]; else n[p.id] = { product: p, qty: q }; return n; });

  const order = useMutation({
    mutationFn: async () => {
      const body = { items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })), address: addr, payment_method: method };
      const o = await api("/orders", { method: "POST", body });
      if (o.status === "pending_payment") { await startPayfastCheckout(o.id, `/orders?pf=1`); return null; }
      return o;
    },
    onSuccess: (o) => { setOpen(false); setCart({}); qc.invalidateQueries({ queryKey: ["orders"] }); if (o) { toast.success("Order placed!"); navigate("/orders"); } },
    onError: (e) => toast.error(e.message),
  });

  const addrValid = addr.name.length >= 2 && addr.phone.length >= 6 && addr.line1.length >= 3 && addr.city.length >= 2 && addr.postal_code.length >= 3;

  return (
    <div data-testid="shop-screen" className="space-y-5">
      <SectionHeader title="Sofit Shop" subtitle="Gear up for your next session." action={count > 0 ? `Cart (${count})` : undefined} onAction={() => setOpen(true)} testID="shop-open-cart" />
      {products.isLoading ? <Loading /> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {(products.data ?? []).map((p) => (
            <div key={p.id} data-testid={`product-${p.id}`} className="rounded-xl2 border border-divider overflow-hidden bg-white">
              <img src={p.image} alt={p.name} className="w-full h-40 object-cover" />
              <div className="p-3">
                <div className="font-display text-base text-ink leading-tight">{p.name}</div>
                <div className="font-body text-xs text-mutedgrey line-clamp-2 min-h-[32px] mt-1">{p.description}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-display text-lg text-brand">{formatR(p.price)}</span>
                  {cart[p.id] ? (
                    <div className="flex items-center gap-2">
                      <button data-testid={`product-dec-${p.id}`} onClick={() => dec(p)} className="w-8 h-8 rounded-full bg-surface2 flex items-center justify-center"><Minus size={14} /></button>
                      <span className="font-display text-ink w-5 text-center">{cart[p.id].qty}</span>
                      <button data-testid={`product-inc-${p.id}`} onClick={() => add(p)} className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center"><Plus size={14} /></button>
                    </div>
                  ) : (
                    <Button testID={`product-add-${p.id}`} title="Add" small disabled={p.stock <= 0} onClick={() => add(p)} />
                  )}
                </div>
                {p.stock <= 0 ? <div className="font-body text-xs text-destructive mt-1">Out of stock</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 rounded-xl2 overflow-hidden" data-testid="shop-cart-dialog">
          <div className="p-5 space-y-3 max-h-[85vh] overflow-y-auto">
            <DialogTitle asChild><h2 className="font-display text-2xl text-ink flex items-center gap-2"><ShoppingBag size={22} className="text-brand" />Your Cart</h2></DialogTitle>
            {items.length === 0 ? <EmptyState icon={Package} title="Cart is empty" /> : (
              <>
                <div className="space-y-2">
                  {items.map((i) => (
                    <div key={i.product.id} className="flex items-center gap-3">
                      <img src={i.product.image} alt="" className="w-12 h-12 rounded-md object-cover" />
                      <div className="flex-1 min-w-0"><div className="font-body font-semibold text-sm text-ink truncate">{i.product.name}</div><div className="font-body text-xs text-mutedgrey">{i.qty} × {formatR(i.product.price)}</div></div>
                      <button onClick={() => dec(i.product)} className="text-mutedgrey"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <div className="font-body font-semibold text-sm text-ink pt-2">Delivery address</div>
                <Field testID="addr-name" placeholder="Full name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} />
                <Field testID="addr-phone" placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
                <Field testID="addr-line1" placeholder="Street address" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Field testID="addr-city" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
                  <Field testID="addr-postal" placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  {[["payfast", "PayFast"], ["wallet", "Demo wallet"]].map(([k, l]) => (
                    <button key={k} data-testid={`shop-pay-${k}`} onClick={() => setMethod(k)} className={`flex-1 h-10 rounded-md border font-body font-semibold text-sm ${method === k ? "border-brand bg-brand-soft2 text-brand" : "border-line text-ink"}`}>{l}</button>
                  ))}
                </div>
                <div className="bg-surface2 rounded-md p-3 space-y-1 font-body text-sm">
                  <div className="flex justify-between"><span className="text-mutedgrey">Subtotal</span><span className="text-ink">{formatR(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-mutedgrey">Delivery</span><span className="text-ink">{formatR(DELIVERY)}</span></div>
                  <div className="flex justify-between border-t border-divider pt-1"><span className="font-display text-ink">Total</span><span className="font-display text-brand" data-testid="shop-total">{formatR(subtotal + DELIVERY)}</span></div>
                </div>
                <Button testID="shop-checkout-button" title={method === "payfast" ? `Pay ${formatR(subtotal + DELIVERY)} with PayFast` : `Place order`} loading={order.isPending} disabled={!addrValid} onClick={() => order.mutate()} full />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
